import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuthStartupConfig } from '@/hooks/useAuthStartupConfig';
import { logger, ErrorHandler } from '@/lib/errorHandling';
import { authLogger } from '@/utils/authLogger';

interface UserProfile {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  address: string | null;
  profileimage: string | null;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, userData?: { name?: string; phone?: string }) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (password: string) => Promise<void>;
  updateProfile: (updates: { name?: string; phone?: string; address?: string; profileimage?: string }) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Configure auth settings on startup
  const { isConfiguring, configResult } = useAuthStartupConfig();

  // Fetch user profile from database
  const fetchUserProfile = async (userId: string) => {
    authLogger.startTimer('profile', 'PROFILE_FETCH');
    authLogger.info('PROFILE_FETCH_START', 'profile', 'Starting user profile fetch', { userId });
    
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      const duration = authLogger.endTimer('profile', 'PROFILE_FETCH');

      if (error) {
        authLogger.error('PROFILE_FETCH_ERROR', 'profile', 'Failed to fetch user profile', { userId, duration }, error);
      } else {
        authLogger.info('PROFILE_FETCH_SUCCESS', 'profile', 'User profile fetched successfully', { 
          userId, 
          hasProfile: !!data,
          profileName: data?.name,
          duration 
        });
        setProfile(data);
      }
    } catch (error) {
      const duration = authLogger.endTimer('profile', 'PROFILE_FETCH');
      authLogger.error('PROFILE_FETCH_EXCEPTION', 'profile', 'Exception during profile fetch', { userId, duration }, error as Error);
    }
  };

  useEffect(() => {
    let isMounted = true;
    authLogger.sessionEvent('AUTH_CONTEXT_INIT', 'Initializing authentication context');

    // Get initial session
    const getInitialSession = async () => {
      authLogger.startTimer('session', 'INITIAL_SESSION_CHECK');
      
      try {
        authLogger.debug('INITIAL_SESSION_START', 'session', 'Starting initial session check');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        const duration = authLogger.endTimer('session', 'INITIAL_SESSION_CHECK');
        
        if (error) {
          authLogger.error('INITIAL_SESSION_ERROR', 'session', 'Error getting initial session', { duration }, error);
        } else if (isMounted) {
          const hasSession = !!session;
          authLogger.info('INITIAL_SESSION_SUCCESS', 'session', 'Initial session check completed', { 
            hasSession,
            userId: session?.user?.id,
            duration 
          });
          
          setSession(session);
          setUser(session?.user ?? null);
          
          if (session?.user) {
            await fetchUserProfile(session.user.id);
          }
        }
      } catch (error) {
        const duration = authLogger.endTimer('session', 'INITIAL_SESSION_CHECK');
        authLogger.error('INITIAL_SESSION_EXCEPTION', 'session', 'Exception during initial session check', { duration }, error as Error);
      } finally {
        if (isMounted) {
          setLoading(false);
          authLogger.debug('AUTH_LOADING_COMPLETE', 'session', 'Authentication loading completed');
        }
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;

        authLogger.sessionEvent('AUTH_STATE_CHANGE', 'Authentication state changed', { 
          event, 
          hasSession: !!session,
          userId: session?.user?.id,
          timestamp: new Date().toISOString()
        });

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Fetch profile when user signs in
        if (session?.user) {
          await fetchUserProfile(session.user.id);
        } else {
          setProfile(null);
        }

        // Handle auth events with toast notifications and logging
        if (event === 'SIGNED_IN') {
          authLogger.authEvent('USER_SIGNED_IN', 'User successfully signed in', { 
            userId: session?.user?.id,
            email: session?.user?.email 
          });
          toast.success("Welcome!", {
            description: "You have successfully signed in.",
          });
        } else if (event === 'SIGNED_OUT') {
          authLogger.authEvent('USER_SIGNED_OUT', 'User signed out');
          toast.success("Signed out", {
            description: "You have been signed out successfully.",
          });
        } else if (event === 'TOKEN_REFRESHED') {
          authLogger.debug('TOKEN_REFRESHED', 'session', 'Auth token refreshed');
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      authLogger.debug('AUTH_CONTEXT_CLEANUP', 'session', 'Authentication context cleaned up');
    };
  }, []);

  const signUp = async (email: string, password: string, userData?: { name?: string; phone?: string }) => {
    authLogger.startTimer('auth', 'SIGN_UP');
    authLogger.authEvent('SIGN_UP_START', 'User registration initiated', { 
      email, 
      hasUserData: !!userData,
      userDataFields: userData ? Object.keys(userData) : []
    });

    try {
      // Auto-detect redirect URL with fallbacks
      const getRedirectUrl = () => {
        const origin = window.location.origin;
        const hostname = window.location.hostname;
        
        // Use specific confirmation page for better UX
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          return `${origin}/auth/confirm`;
        }
        
        // For production, use confirmation page
        return `${origin}/auth/confirm`;
      };

      const redirectUrl = getRedirectUrl();
      authLogger.debug('SIGN_UP_REDIRECT_URL', 'auth', 'Using redirect URL for signup', { redirectUrl });

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            name: userData?.name,
            phone: userData?.phone,
          }
        }
      });

      const duration = authLogger.endTimer('auth', 'SIGN_UP');

      if (error) {
        authLogger.authError('SIGN_UP_ERROR', 'User registration failed', error, { 
          email, 
          errorCode: error.status,
          duration 
        });

        // Handle specific errors with better messaging
        if (error.message.includes('User already registered')) {
          toast.error("Account already exists", {
            description: "This email is already registered. Try signing in instead.",
          });
        } else if (error.message.includes('Invalid email')) {
          toast.error("Invalid email", {
            description: "Please enter a valid email address.",
          });
        } else if (error.message.includes('Password')) {
          toast.error("Password requirements not met", {
            description: "Password must be at least 6 characters long.",
          });
        } else {
          toast.error("Sign up failed", {
            description: error.message,
          });
        }
      } else if (data.user && !data.session) {
        authLogger.authEvent('SIGN_UP_SUCCESS_PENDING', 'User registration successful, email confirmation required', { 
          userId: data.user.id,
          email: data.user.email,
          needsConfirmation: true,
          duration
        });

        toast.success("Account created!", {
          description: "Check your email for a confirmation link to activate your account.",
        });
      } else if (data.session) {
        authLogger.authEvent('SIGN_UP_SUCCESS_COMPLETE', 'User registration and sign-in completed', { 
          userId: data.user?.id,
          email: data.user?.email,
          autoConfirmed: true,
          duration
        });

        // Auto-confirmed, user is signed in
        toast.success("Welcome to Plant Autopilot!", {
          description: "Your account has been created and you're now signed in.",
        });
      }

      return { error };
    } catch (error) {
      const duration = authLogger.endTimer('auth', 'SIGN_UP');
      authLogger.authError('SIGN_UP_EXCEPTION', 'Exception during user registration', error as Error, { 
        email, 
        duration 
      });
      
      // Handle network and other errors
      toast.error("Registration failed", {
        description: "Please check your internet connection and try again.",
      });
      
      return { error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    authLogger.startTimer('auth', 'SIGN_IN');
    authLogger.authEvent('SIGN_IN_START', 'User sign-in initiated', { email });

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      const duration = authLogger.endTimer('auth', 'SIGN_IN');

      if (error) {
        authLogger.authError('SIGN_IN_ERROR', 'User sign-in failed', error, { 
          email, 
          errorCode: error.status,
          duration 
        });
        
        toast.error("Sign in failed", {
          description: error.message,
        });
      } else {
        authLogger.authEvent('SIGN_IN_SUCCESS', 'User sign-in successful', { 
          email, 
          duration 
        });
      }

      return { error };
    } catch (error) {
      const duration = authLogger.endTimer('auth', 'SIGN_IN');
      authLogger.authError('SIGN_IN_EXCEPTION', 'Exception during user sign-in', error as Error, { 
        email, 
        duration 
      });
      return { error: error as AuthError };
    }
  };

  const signOut = async () => {
    authLogger.startTimer('auth', 'SIGN_OUT');
    authLogger.authEvent('SIGN_OUT_START', 'User sign-out initiated');

    try {
      const { error } = await supabase.auth.signOut();
      const duration = authLogger.endTimer('auth', 'SIGN_OUT');
      
      if (error) {
        authLogger.authError('SIGN_OUT_ERROR', 'User sign-out failed', error, { duration });
        toast.error("Error signing out", {
          description: error.message,
        });
      } else {
        authLogger.authEvent('SIGN_OUT_SUCCESS', 'User sign-out successful', { duration });
      }
    } catch (error) {
      const duration = authLogger.endTimer('auth', 'SIGN_OUT');
      authLogger.authError('SIGN_OUT_EXCEPTION', 'Exception during user sign-out', error as Error, { duration });
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const getResetRedirectUrl = () => {
        const origin = window.location.origin;
        return `${origin}/auth/reset-password`;
      };

      const redirectUrl = getResetRedirectUrl();
      console.log('Using reset redirect URL:', redirectUrl);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        if (error.message.includes('User not found')) {
          toast.error("Email not found", {
            description: "No account found with this email address.",
          });
        } else {
          toast.error("Password reset failed", {
            description: error.message,
          });
        }
      } else {
        toast.success("Password reset sent!", {
          description: "Check your email for a secure password reset link.",
        });
      }

      return { error };
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error("Reset request failed", {
        description: "Please check your internet connection and try again.",
      });
      return { error: error as AuthError };
    }
  };

  const updatePassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        toast.error("Password update failed", {
          description: error.message,
        });
        throw error;
      }

      toast.success("Password updated", {
        description: "Your password has been updated successfully.",
      });
    } catch (error) {
      console.error('Password update error:', error);
      throw error;
    }
  };

  const updateProfile = async (updates: { name?: string; phone?: string; address?: string; profileimage?: string }) => {
    if (!user) {
      return { error: new Error('No user logged in') };
    }

    try {
      const { error } = await supabase
        .from('user_profiles')
        .upsert({
          id: user.id,
          email: user.email!,
          ...updates,
          updated_at: new Date().toISOString()
        });

      if (error) {
        toast.error("Profile update failed", {
          description: error.message,
        });
        return { error: new Error(error.message) };
      }

      // Refresh profile data
      await fetchUserProfile(user.id);

      toast.success("Profile updated", {
        description: "Your profile has been updated successfully.",
      });

      return { error: null };
    } catch (error) {
      console.error('Profile update error:', error);
      return { error: error as Error };
    }
  };

  const value = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
